import { pence } from '@cf/shared';
import { assertReviewed } from './review.js';
import { excessOver, mulBps } from './bps.js';
/**
 * Income-contingent student loan repayments on an ANNUAL basis for one loan.
 *
 * A student loan repayment is NOT tax. It is collected through PAYE alongside
 * income tax and National Insurance, but it is the repayment of a debt: it must
 * never be folded into a client's tax figure, or the tool would disagree with
 * the P60 sitting in front of the adviser.
 *
 * What is modelled: the deduction only. The outstanding BALANCE is deliberately
 * not modelled in v1 (see docs/features/2026-08-26-student-loans.md). Without
 * the statutory interest rates a balance amortises far too fast, repayments
 * stop early, take-home rises early, and projected wealth and the estate are
 * both overstated — a wrong figure that flatters the client is worse than an
 * absent one. Repayments therefore stop on the WRITE-OFF year alone.
 */
/** Plan 4 exists (Scotland/SAAS-funded) but is out of scope — see below. */
export class StudentLoanPlanNotSupportedError extends Error {
    constructor(plan) {
        super(`Student loan ${plan} is outside Phase 1 scope: only Plan 1, Plan 2, Plan 5 and the ` +
            'Postgraduate Loan are implemented. Plan 4 (Scottish/SAAS-funded study) has its own ' +
            'threshold and term, and no other plan may be substituted for it. This client cannot ' +
            'be projected until the Plan 4 rules ship.');
        this.name = 'StudentLoanPlanNotSupportedError';
    }
}
/** Thrown when the active rule set carries no student-loan figures at all. */
export class StudentLoanRulesUnavailableError extends Error {
    constructor(taxYear) {
        super(`No student loan thresholds are published in the ${taxYear} rule set. Repayments cannot ` +
            'be modelled on this basis: deducting nothing would silently overstate take-home pay ' +
            'for the whole projection. Choose a tax basis that carries student loan figures, or ' +
            'remove the loan.');
        this.name = 'StudentLoanRulesUnavailableError';
    }
}
/**
 * One loan's annual repayment: `rate × earnings above the plan threshold`.
 *
 * ROUNDING: HMRC rounds a student loan deduction DOWN to whole pounds (the
 * repayment tables and the PAYE calculation both discard the pence), so the
 * borrower is never asked for a part-pound. We round the ANNUAL figure down
 * once rather than rounding twelve monthly deductions, which is the annual
 * basis every other calculator in this package works on. The direction is
 * documented in docs/CALCULATION_METHOD.md: rounding down understates the
 * modelled repayment by at most 99p a year, which is the conservative
 * direction for take-home pay.
 */
export function studentLoanRepayment(rules, input) {
    assertReviewed(rules);
    const plans = rules.studentLoans;
    if (plans === undefined) {
        throw new StudentLoanRulesUnavailableError(rules.taxYear);
    }
    const plan = plans[input.plan];
    if (plan === undefined) {
        throw new StudentLoanPlanNotSupportedError(input.plan);
    }
    const earnings = input.earnings ?? pence(0);
    if (!Number.isSafeInteger(earnings) || earnings < 0) {
        throw new RangeError('studentLoanRepayment earnings must be a non-negative integer of pence');
    }
    // Income-contingent for real: below the threshold this is exactly zero, and
    // nothing is carried into a later year. A year out of work repays nothing
    // and never catches up.
    const above = excessOver(earnings, plan.thresholdAnnual);
    const exact = mulBps(above, plan.rateBps);
    return {
        repayment: pence(Math.trunc(exact / 100) * 100),
        threshold: plan.thresholdAnnual,
        rateBps: plan.rateBps,
    };
}
/**
 * The last projection year in which a loan is still repaid. Repayments run from
 * the first April the loan fell due and stop when the plan's term is reached:
 * the write-off year itself and everything after it repay nothing.
 *
 * Time-anchored to the FIRST-DUE year only (FEATURES §0.2). "Age 65 for the
 * oldest Plan 1 loans" is a consequence of when those loans fell due, not an
 * input — changing a date of birth alone must never move this year.
 */
export function studentLoanWriteOffYear(rules, plan, firstRepaymentDueYear) {
    assertReviewed(rules);
    const plans = rules.studentLoans;
    if (plans === undefined) {
        throw new StudentLoanRulesUnavailableError(rules.taxYear);
    }
    const p = plans[plan];
    if (p === undefined) {
        throw new StudentLoanPlanNotSupportedError(plan);
    }
    if (!Number.isInteger(firstRepaymentDueYear)) {
        throw new RangeError('firstRepaymentDueYear must be a whole year');
    }
    return firstRepaymentDueYear + p.writeOffYears;
}
/** True when `year` falls inside the loan's repayment window. */
export function studentLoanRepayable(rules, plan, firstRepaymentDueYear, year) {
    return (year >= firstRepaymentDueYear &&
        year < studentLoanWriteOffYear(rules, plan, firstRepaymentDueYear));
}
//# sourceMappingURL=student-loan.js.map